import { keys } from 'remeda';

import type { AiModel, UsedAiModel } from '../chat';

export type AiProviderName = 'anthropic' | 'google' | 'openai';

/**
 * Input is billed at three rates. Tokens read from the provider's prompt cache
 * are the cheap ones, tokens written to it can carry a surcharge, and the rest
 * pay the plain input price. A model with no write surcharge sets the write
 * price equal to its input price.
 *
 * Anthropic write prices are for the 5-minute cache, which is what
 * `cacheControl: { type: 'ephemeral' }` with no `ttl` selects. The 1-hour cache
 * costs 2x input to write, so a call that sets `ttl: '1h'` is priced too low
 * until the catalog can tell the two apart.
 */
export type AiModelPricing = {
  cacheReadMicrodollarsPerMillionTokens: number;
  cacheWriteMicrodollarsPerMillionTokens: number;
  inputMicrodollarsPerMillionTokens: number;
  outputMicrodollarsPerMillionTokens: number;
};

export const REASONING_EFFORTS = {
  high: 'high',
  low: 'low',
  max: 'max',
  medium: 'medium',
  xhigh: 'xhigh',
} as const;

export type ReasoningEffort = (typeof REASONING_EFFORTS)[keyof typeof REASONING_EFFORTS];

/**
 * Efforts ordered weakest to strongest. Index into this array is the
 * comparison key — there is no numeric ordering on the union itself.
 */
export const REASONING_EFFORT_LADDER = [
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
] as const satisfies readonly ReasoningEffort[];

/**
 * How a model expresses reasoning depth. The provider surface differs enough
 * that one shape can't cover all three, so this is a discriminated union and
 * `buildProviderOptions` switches on `style`.
 *
 * - `anthropicAdaptive` — Claude 5 family: `thinking: { type: 'adaptive' }`
 *   plus `effort`. `disableRequiresEffortAtMost` is the ceiling at which
 *   `thinking: { type: 'disabled' }` is still accepted; above it the API
 *   returns 400. A null ceiling means thinking cannot be disabled.
 * - `anthropicManualBudget` — Claude 4.5 Haiku: rejects both adaptive thinking
 *   and `effort`, so effort is mapped to an explicit `budgetTokens`. The
 *   provider minimum is 1024 and the budget must stay below the request's
 *   `maxOutputTokens`.
 * - `openaiEffort` — Responses API `reasoningEffort`. Ceiling varies by model
 *   family; `supportsNone` distinguishes models that can disable reasoning.
 * - `googleThinkingBudget` — `thinkingConfig.thinkingBudget`, an explicit
 *   token count like the Anthropic manual path. Thinking cannot be turned off:
 *   every Gemini model in this catalog rejects a zero budget ("Budget 0 is
 *   invalid. This model only works in thinking mode."), so "off" resolves to
 *   the smallest budget rather than to no thinking at all.
 * - `googleThinkingLevel` — `thinkingConfig.thinkingLevel`, a named level.
 *   Gemini 3.7+ replaces the numeric budget with this enum and rejects
 *   `thinkingBudget` outright. Like the budget path, thinking cannot be turned
 *   off, so "off" resolves to the weakest supported level.
 */
export type ReasoningCapability =
  | {
      disableRequiresEffortAtMost: ReasoningEffort | null;
      style: 'anthropicAdaptive';
      supportedEfforts: readonly ReasoningEffort[];
      supportsBlockBinding: boolean;
      defaultThinkingDisplay?: 'updates';
    }
  | { effortBudgetTokens: Record<ReasoningEffort, number>; style: 'anthropicManualBudget' }
  | { effortBudgetTokens: Record<ReasoningEffort, number>; style: 'googleThinkingBudget' }
  | { effortLevels: Record<ReasoningEffort, GoogleThinkingLevel>; style: 'googleThinkingLevel' }
  | { style: 'openaiEffort'; supportedEfforts: readonly ReasoningEffort[]; supportsNone: boolean };

/**
 * The `thinkingLevel` values Gemini 3.8 Flash accepts. The wire enum also
 * defines `minimal`, but the model rejects it, so it is deliberately absent.
 */
export type GoogleThinkingLevel = 'low' | 'medium' | 'high';

/**
 * Effort ladder for providers that take an explicit token budget rather than a
 * named level. Values below `low` would fall under Anthropic's 1024 minimum.
 */
const BUDGETED_EFFORT_TOKENS: Record<ReasoningEffort, number> = {
  high: 5_000,
  low: 1_024,
  max: 16_000,
  medium: 2_500,
  xhigh: 10_000,
};

const ALL_EFFORTS = REASONING_EFFORT_LADDER;

// Efforts above `high` collapse onto it: `high` is the deepest level Gemini
// 3.7 Flash defines.
const GOOGLE_THINKING_LEVELS: Record<ReasoningEffort, GoogleThinkingLevel> = {
  high: 'high',
  low: 'low',
  max: 'high',
  medium: 'medium',
  xhigh: 'high',
};

const OPENAI_EFFORTS_THROUGH_MAX = REASONING_EFFORT_LADDER;

/**
 * A model we no longer call. Retired aliases cannot be deleted because stored
 * `AiTokenUsage.modelKey` rows reference them, but they carry no live routing
 * data: no provider model id, no pricing (historical cost is already persisted
 * on each usage row), and no fallback chain.
 */
type RetiredAiModelEntry = {
  contextWindowTokens: number;
  label: string;
  maxOutputTokens: number;
  provider: AiProviderName;
  status: 'retired';
};

/**
 * A model that can currently be called. `fallbackChain` always begins with the
 * model itself — `ai-fallback` walks the chain in order when a provider errors.
 */
type ActiveAiModelEntry = {
  contextWindowTokens: number;
  fallbackChain: readonly UsedAiModel[];
  label: string;
  maxOutputTokens: number;
  pricing: AiModelPricing;
  provider: AiProviderName;
  providerModelId: string;
  reasoning: ReasoningCapability;
  status: 'active';
  /**
   * Optional ceiling for input budgets derived from the context window, for
   * models whose pricing steps up past a threshold. GPT-6 bills a request
   * above 272K input tokens at 2x input / 1.5x output for the *whole* request
   * (https://developers.openai.com/api/docs/models/gpt-6-luna), so a
   * cost-optimized tier caps below that rather than floating into the
   * surcharge band on its 1M window. Premium tiers stay uncapped —
   * capability is the point there and their callers accept the pricing.
   */
  baseRateInputTokenCap?: number;
  /** Provider input ceiling, separate from the combined context window. */
  maxInputTokens?: number;
  supportsForcedToolChoice?: boolean;
};

export type AiModelCatalogEntry = ActiveAiModelEntry | RetiredAiModelEntry;
export type ActiveAiModelSpec = ActiveAiModelEntry;

/**
 * Single source of truth for every model alias: provider, provider model id,
 * display label, context window, max output, pricing, and fallback chain.
 *
 * Adding a model means adding one entry here. `satisfies Record<AiModel, …>`
 * makes a missing entry a compile error.
 *
 * Pricing is per 1M tokens. Check current vendor pricing before updating:
 * - Anthropic: https://platform.claude.com/docs/en/about-claude/pricing
 * - Google Gemini: https://ai.google.dev/gemini-api/docs/pricing
 * - OpenAI: https://openai.com/api/pricing/
 */
/**
 * Grouped active-first, then retired, so the 9 models that can actually be
 * called aren't interleaved with the retired entries that only exist to label stored usage
 * rows. That grouping is worth more here than alphabetical ordering.
 */
// eslint-disable-next-line sort-keys
export const AI_MODEL_CATALOG = {
  claude55Opus: {
    contextWindowTokens: 1_048_576,
    fallbackChain: ['claude55Opus', 'openai61Sol', 'gemini25Pro'],
    label: 'Claude Opus 5.5',
    maxOutputTokens: 128_000,
    pricing: {
      cacheReadMicrodollarsPerMillionTokens: 200_000,
      cacheWriteMicrodollarsPerMillionTokens: 5_000_000,
      inputMicrodollarsPerMillionTokens: 4_000_000,
      outputMicrodollarsPerMillionTokens: 20_000_000,
    },
    provider: 'anthropic',
    providerModelId: 'claude-opus-5-5',
    /** Opus 5.5 always thinks; effort controls its depth. */
    reasoning: {
      disableRequiresEffortAtMost: null,
      style: 'anthropicAdaptive',
      supportedEfforts: ALL_EFFORTS,
      supportsBlockBinding: true,
    },
    status: 'active',
    supportsForcedToolChoice: false,
  },
  claude55Sonnet: {
    contextWindowTokens: 1_048_576,
    fallbackChain: ['claude55Sonnet', 'openai61Sol', 'gemini25Pro'],
    label: 'Claude Sonnet 5.5',
    maxOutputTokens: 128_000,
    pricing: {
      cacheReadMicrodollarsPerMillionTokens: 200_000,
      cacheWriteMicrodollarsPerMillionTokens: 2_500_000,
      inputMicrodollarsPerMillionTokens: 2_000_000,
      outputMicrodollarsPerMillionTokens: 10_000_000,
    },
    provider: 'anthropic',
    providerModelId: 'claude-sonnet-5-5',
    /** Use adaptive thinking, including low effort when a workload requests off. */
    reasoning: {
      defaultThinkingDisplay: 'updates',
      disableRequiresEffortAtMost: null,
      style: 'anthropicAdaptive',
      supportedEfforts: ALL_EFFORTS,
      supportsBlockBinding: true,
    },
    status: 'active',
    supportsForcedToolChoice: false,
  },
  claude45Haiku: {
    contextWindowTokens: 200_000,
    fallbackChain: ['claude45Haiku', 'openai6Luna', 'gemini38Flash'],
    label: 'Claude 4.5 Haiku',
    maxOutputTokens: 64_000,
    pricing: {
      cacheReadMicrodollarsPerMillionTokens: 100_000,
      cacheWriteMicrodollarsPerMillionTokens: 1_250_000,
      inputMicrodollarsPerMillionTokens: 1_000_000,
      outputMicrodollarsPerMillionTokens: 5_000_000,
    },
    provider: 'anthropic',
    providerModelId: 'claude-haiku-4-5',
    /**
     * Haiku 4.5 predates both adaptive thinking and `effort` — sending either
     * returns a 400. Effort is mapped to an explicit `budgetTokens` instead.
     */
    reasoning: { effortBudgetTokens: BUDGETED_EFFORT_TOKENS, style: 'anthropicManualBudget' },
    status: 'active',
  },
  /**
   * Was gemini-3-pro-preview until Google shut it down 2026-03-09. Using
   * gemini-2.5-pro (stable GA) until a stable Gemini 3 Pro is released.
   * Still waiting as of 2026-09: the only Gemini 3 Pro is preview-only
   * (`gemini-3.1-pro-preview`), and Google's preview retirement notice can
   * be as short as two weeks, so it is not safe to depend on here. When a
   * stable Gemini 3 Pro ships, add a new alias and retire this entry while
   * preserving its identity in stored usage records.
   */
  gemini25Pro: {
    contextWindowTokens: 1_048_576,
    fallbackChain: ['gemini25Pro', 'openai61Sol', 'claude55Sonnet'],
    label: 'Gemini 2.5 Pro',
    maxOutputTokens: 65_536,
    pricing: {
      cacheReadMicrodollarsPerMillionTokens: 125_000,
      cacheWriteMicrodollarsPerMillionTokens: 1_250_000,
      inputMicrodollarsPerMillionTokens: 1_250_000,
      outputMicrodollarsPerMillionTokens: 10_000_000,
    },
    provider: 'google',
    providerModelId: 'gemini-2.5-pro',
    reasoning: { effortBudgetTokens: BUDGETED_EFFORT_TOKENS, style: 'googleThinkingBudget' },
    status: 'active',
  },
  gemini35FlashLite: {
    contextWindowTokens: 1_048_576,
    fallbackChain: ['gemini35FlashLite', 'openai6Luna', 'claude45Haiku'],
    label: 'Gemini 3.5 Flash Lite',
    maxOutputTokens: 65_536,
    pricing: {
      cacheReadMicrodollarsPerMillionTokens: 30_000,
      cacheWriteMicrodollarsPerMillionTokens: 300_000,
      inputMicrodollarsPerMillionTokens: 300_000,
      outputMicrodollarsPerMillionTokens: 2_500_000,
    },
    provider: 'google',
    providerModelId: 'gemini-3.5-flash-lite',
    reasoning: { effortBudgetTokens: BUDGETED_EFFORT_TOKENS, style: 'googleThinkingBudget' },
    status: 'active',
  },
  gemini38Flash: {
    contextWindowTokens: 1_048_576,
    fallbackChain: ['gemini38Flash', 'openai6Luna', 'claude45Haiku'],
    label: 'Gemini 3.8 Flash',
    maxOutputTokens: 65_536,
    /**
     * Post-promo list price effective 2027-01-01; Google bills $0.75/$3.75
     * through 2026-12-31, and $0.075 for a cached read. Priced at the list
     * rate so cost accounting doesn't silently understate once the promo
     * lapses.
     */
    pricing: {
      cacheReadMicrodollarsPerMillionTokens: 150_000,
      cacheWriteMicrodollarsPerMillionTokens: 1_500_000,
      inputMicrodollarsPerMillionTokens: 1_500_000,
      outputMicrodollarsPerMillionTokens: 7_500_000,
    },
    provider: 'google',
    providerModelId: 'gemini-3.8-flash',
    reasoning: { effortLevels: GOOGLE_THINKING_LEVELS, style: 'googleThinkingLevel' },
    status: 'active',
  },

  openai6Luna: {
    baseRateInputTokenCap: 250_000,
    contextWindowTokens: 1_050_000,
    fallbackChain: ['openai6Luna', 'claude45Haiku', 'gemini38Flash'],
    label: 'GPT 6 Luna',
    maxInputTokens: 922_000,
    maxOutputTokens: 128_000,
    pricing: {
      cacheReadMicrodollarsPerMillionTokens: 10_000,
      cacheWriteMicrodollarsPerMillionTokens: 125_000,
      inputMicrodollarsPerMillionTokens: 100_000,
      outputMicrodollarsPerMillionTokens: 500_000,
    },
    provider: 'openai',
    providerModelId: 'gpt-6-luna',
    reasoning: {
      style: 'openaiEffort',
      supportedEfforts: OPENAI_EFFORTS_THROUGH_MAX,
      supportsNone: true,
    },
    status: 'active',
  },
  openai61Sol: {
    contextWindowTokens: 1_050_000,
    fallbackChain: ['openai61Sol', 'claude55Opus', 'gemini25Pro'],
    label: 'GPT 6.1 Sol',
    maxInputTokens: 922_000,
    maxOutputTokens: 128_000,
    pricing: {
      cacheReadMicrodollarsPerMillionTokens: 100_000,
      cacheWriteMicrodollarsPerMillionTokens: 2_500_000,
      inputMicrodollarsPerMillionTokens: 2_000_000,
      outputMicrodollarsPerMillionTokens: 10_000_000,
    },
    provider: 'openai',
    providerModelId: 'gpt-6.1-sol',
    reasoning: {
      style: 'openaiEffort',
      supportedEfforts: OPENAI_EFFORTS_THROUGH_MAX,
      supportsNone: false,
    },
    status: 'active',
  },
  openai6Astra: {
    contextWindowTokens: 1_050_000,
    fallbackChain: ['openai6Astra', 'claude55Opus', 'gemini25Pro'],
    label: 'GPT 6 Astra',
    maxInputTokens: 922_000,
    maxOutputTokens: 128_000,
    pricing: {
      cacheReadMicrodollarsPerMillionTokens: 1_000_000,
      cacheWriteMicrodollarsPerMillionTokens: 12_500_000,
      inputMicrodollarsPerMillionTokens: 10_000_000,
      outputMicrodollarsPerMillionTokens: 50_000_000,
    },
    provider: 'openai',
    providerModelId: 'gpt-6-astra',
    reasoning: {
      style: 'openaiEffort',
      supportedEfforts: OPENAI_EFFORTS_THROUGH_MAX,
      supportsNone: false,
    },
    status: 'active',
  },

  // ==========================================================================
  // Retired — kept so stored AiTokenUsage rows still resolve to a label.
  // ==========================================================================

  openai6Sol: {
    contextWindowTokens: 1_050_000,
    label: 'GPT 6 Sol',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },
  claude5Sonnet: {
    contextWindowTokens: 1_048_576,
    label: 'Claude Sonnet 5',
    maxOutputTokens: 128_000,
    provider: 'anthropic',
    status: 'retired',
  },
  claude5Opus: {
    contextWindowTokens: 1_048_576,
    label: 'Claude Opus 5',
    maxOutputTokens: 128_000,
    provider: 'anthropic',
    status: 'retired',
  },
  gemini37Flash: {
    contextWindowTokens: 1_048_576,
    label: 'Gemini 3.7 Flash',
    maxOutputTokens: 65_536,
    provider: 'google',
    status: 'retired',
  },
  openai56Sol: {
    contextWindowTokens: 1_048_576,
    label: 'GPT 5.6 Sol',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },
  openai56Terra: {
    contextWindowTokens: 1_048_576,
    label: 'GPT 5.6 Terra',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },
  openai56Luna: {
    contextWindowTokens: 1_048_576,
    label: 'GPT 5.6 Luna',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },
  openai55: {
    contextWindowTokens: 1_048_576,
    label: 'GPT 5.5',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },
  openai54Mini: {
    contextWindowTokens: 400_000,
    label: 'GPT 5.4 Mini',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },
  openai54Nano: {
    contextWindowTokens: 400_000,
    label: 'GPT 5.4 Nano',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },

  /**
   * Anthropic gates Claude Fable 5 behind a 30-day data-retention requirement
   * that our zero-data-retention org does not meet, so requests to it fail
   * with `model_not_available`.
   */
  claude5Fable: {
    contextWindowTokens: 1_048_576,
    label: 'Claude Fable 5',
    maxOutputTokens: 128_000,
    provider: 'anthropic',
    status: 'retired',
  },
  claude37Sonnet: {
    contextWindowTokens: 200_000,
    label: 'Claude 3.7 Sonnet',
    maxOutputTokens: 128_000,
    provider: 'anthropic',
    status: 'retired',
  },
  claude45Sonnet: {
    contextWindowTokens: 200_000,
    label: 'Claude 4.5 Sonnet',
    maxOutputTokens: 64_000,
    provider: 'anthropic',
    status: 'retired',
  },
  claude46Opus: {
    contextWindowTokens: 1_048_576,
    label: 'Claude 4.6 Opus',
    maxOutputTokens: 128_000,
    provider: 'anthropic',
    status: 'retired',
  },
  claude46Sonnet: {
    contextWindowTokens: 1_048_576,
    label: 'Claude 4.6 Sonnet',
    maxOutputTokens: 64_000,
    provider: 'anthropic',
    status: 'retired',
  },
  claude47Opus: {
    contextWindowTokens: 1_048_576,
    label: 'Claude 4.7 Opus',
    maxOutputTokens: 128_000,
    provider: 'anthropic',
    status: 'retired',
  },
  claude48Opus: {
    contextWindowTokens: 1_048_576,
    label: 'Claude 4.8 Opus',
    maxOutputTokens: 128_000,
    provider: 'anthropic',
    status: 'retired',
  },
  claude4Sonnet: {
    contextWindowTokens: 200_000,
    label: 'Claude 4 Sonnet',
    maxOutputTokens: 64_000,
    provider: 'anthropic',
    status: 'retired',
  },
  gemini25Flash: {
    contextWindowTokens: 1_048_576,
    label: 'Gemini 2.5 Flash',
    maxOutputTokens: 65_536,
    provider: 'google',
    status: 'retired',
  },
  gemini25FlashLite: {
    contextWindowTokens: 1_048_576,
    label: 'Gemini 2.5 Flash Lite',
    maxOutputTokens: 65_536,
    provider: 'google',
    status: 'retired',
  },
  gemini31FlashLite: {
    contextWindowTokens: 1_048_576,
    label: 'Gemini 3.1 Flash Lite',
    maxOutputTokens: 65_536,
    provider: 'google',
    status: 'retired',
  },
  gemini35Flash: {
    contextWindowTokens: 1_048_576,
    label: 'Gemini 3.5 Flash',
    maxOutputTokens: 65_536,
    provider: 'google',
    status: 'retired',
  },
  gemini36Flash: {
    contextWindowTokens: 1_048_576,
    label: 'Gemini 3.6 Flash',
    maxOutputTokens: 65_536,
    provider: 'google',
    status: 'retired',
  },
  gemini3Pro: {
    contextWindowTokens: 1_048_576,
    label: 'Gemini 3 Pro',
    maxOutputTokens: 65_536,
    provider: 'google',
    status: 'retired',
  },
  openai41: {
    contextWindowTokens: 1_048_576,
    label: 'GPT 4.1',
    maxOutputTokens: 32_768,
    provider: 'openai',
    status: 'retired',
  },
  openai41Mini: {
    contextWindowTokens: 1_048_576,
    label: 'GPT 4.1 Mini',
    maxOutputTokens: 32_768,
    provider: 'openai',
    status: 'retired',
  },
  openai4o: {
    contextWindowTokens: 128_000,
    label: 'GPT 4o',
    maxOutputTokens: 16_384,
    provider: 'openai',
    status: 'retired',
  },
  openai4omini: {
    contextWindowTokens: 128_000,
    label: 'GPT 4o Mini',
    maxOutputTokens: 16_384,
    provider: 'openai',
    status: 'retired',
  },
  openai5: {
    contextWindowTokens: 400_000,
    label: 'GPT 5',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },
  openai51: {
    contextWindowTokens: 400_000,
    label: 'GPT 5.1',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },
  openai52: {
    contextWindowTokens: 400_000,
    label: 'GPT 5.2',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },
  openai54: {
    contextWindowTokens: 1_048_576,
    label: 'GPT 5.4',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },
  openai5Mini: {
    contextWindowTokens: 400_000,
    label: 'GPT 5 Mini',
    maxOutputTokens: 128_000,
    provider: 'openai',
    status: 'retired',
  },
  openaio1: {
    contextWindowTokens: 200_000,
    label: 'OpenAI o1',
    maxOutputTokens: 100_000,
    provider: 'openai',
    status: 'retired',
  },
  openaio3: {
    contextWindowTokens: 200_000,
    label: 'OpenAI o3',
    maxOutputTokens: 100_000,
    provider: 'openai',
    status: 'retired',
  },
  openaio3Mini: {
    contextWindowTokens: 200_000,
    label: 'OpenAI o3 Mini',
    maxOutputTokens: 100_000,
    provider: 'openai',
    status: 'retired',
  },
} satisfies Record<AiModel, AiModelCatalogEntry>;

export function getIsActiveAiModelEntry(entry: AiModelCatalogEntry): entry is ActiveAiModelSpec {
  return entry.status === 'active';
}

/** Every alias in the catalog, retired ones included. */
export const AI_MODEL_KEYS = keys(AI_MODEL_CATALOG);

/**
 * `status: 'active'` and membership in `UsedAiModel` are the same fact stated
 * two ways — the catalog is the source of truth and a spec test pins the two
 * in sync, so narrowing on `status` is sound.
 */
export function getIsUsedAiModel(model: AiModel): model is UsedAiModel {
  return AI_MODEL_CATALOG[model].status === 'active';
}

/** Every alias that can currently be called. */
export const USED_AI_MODEL_KEYS: UsedAiModel[] = AI_MODEL_KEYS.filter(getIsUsedAiModel);

/**
 * Resolve a spec from the provider's own model id rather than our alias.
 * Needed by model wrappers, which run at the SDK boundary and only see
 * `modelId` — notably after `ai-fallback` has switched models mid-call.
 */
export function getActiveAiModelSpecByProviderModelId(
  providerModelId: string
): ActiveAiModelSpec | undefined {
  const model = USED_AI_MODEL_KEYS.find(
    key => requireActiveAiModelSpec(key).providerModelId === providerModelId
  );
  return model ? requireActiveAiModelSpec(model) : undefined;
}

/**
 * Lower `effort` to the strongest level the model actually accepts. Requesting
 * `xhigh` from a model whose ceiling is `high` would 400, so callers can ask
 * for the level they want and let the model decide what it can honor.
 */
export function clampReasoningEffort(
  effort: ReasoningEffort,
  supportedEfforts: readonly ReasoningEffort[]
): ReasoningEffort {
  const supported = new Set(supportedEfforts);
  if (supported.has(effort)) return effort;

  // Walk up to the requested rank and keep the strongest supported level seen.
  // Every capability lists at least `low`, so the seed is never returned as-is
  // for a well-formed catalog entry.
  let clamped: ReasoningEffort = 'low';
  for (const candidate of REASONING_EFFORT_LADDER) {
    if (candidate === effort) break;
    if (supported.has(candidate)) clamped = candidate;
  }

  return clamped;
}

/**
 * Whether `thinking: { type: 'disabled' }` is legal for this model at the given
 * effort. A null Anthropic ceiling and an OpenAI model without `none`
 * support both mean reasoning must remain enabled.
 */
export function getCanDisableThinking(spec: ActiveAiModelSpec, effort: ReasoningEffort): boolean {
  const { reasoning } = spec;
  // Gemini has no "off" to express: budget models reject a zero budget, and
  // level models reject `minimal`.
  if (reasoning.style === 'googleThinkingBudget' || reasoning.style === 'googleThinkingLevel')
    return false;
  if (reasoning.style === 'openaiEffort') return reasoning.supportsNone;
  if (reasoning.style !== 'anthropicAdaptive') return true;
  if (reasoning.disableRequiresEffortAtMost === null) return false;

  return (
    REASONING_EFFORT_LADDER.indexOf(effort) <=
    REASONING_EFFORT_LADDER.indexOf(reasoning.disableRequiresEffortAtMost)
  );
}

/**
 * Spec for a model that can currently be called, or `undefined` if the alias
 * is retired.
 */
export function getActiveAiModelSpec(model: AiModel): ActiveAiModelSpec | undefined {
  const entry = AI_MODEL_CATALOG[model];
  return getIsActiveAiModelEntry(entry) ? entry : undefined;
}

/**
 * Spec for a callable model. Total over `UsedAiModel` by construction — every
 * member of that union is an active catalog entry, pinned by a spec test. The
 * throw is an unreachable invariant guard, not a runtime code path.
 */
export function requireActiveAiModelSpec(model: UsedAiModel): ActiveAiModelSpec {
  const entry = AI_MODEL_CATALOG[model];
  if (!getIsActiveAiModelEntry(entry))
    throw new Error(`AI model "${model}" is in UsedAiModel but marked retired in the catalog.`);

  return entry;
}

/**
 * Models that are not chat completions and so have no place in
 * `AI_MODEL_CATALOG` — no context window, fallback chain, or reasoning
 * capability applies. Recorded here so every provider model id the system
 * sends is findable in one file.
 *
 * Cohere reranking is deliberately absent: its model id is validated in
 * `packages/server/src/services/cohere/cohere.ts` against the Cohere SDK's own
 * model union, and relocating the literal here would trade that compile-time
 * guarantee for co-location.
 */
export const NON_CHAT_MODELS = {
  embeddingLarge: {
    defaultDimensions: 3072,
    provider: 'openai',
    providerModelId: 'openai:text-embedding-3-large',
  },
  embeddingSmall: {
    defaultDimensions: 1536,
    provider: 'openai',
    providerModelId: 'openai:text-embedding-3-small',
  },
} as const satisfies Record<
  string,
  { defaultDimensions: number; provider: AiProviderName; providerModelId: string }
>;
